import type { PacketSourceResolved } from "../../lib/api";
import { CalendarDays, MapPin, User } from "lucide-react";

/** Q42: calendar-event packet renderer.
 *
 *  Outlook + Google share this view. We render from meta_json
 *  (organizer / attendees / location / start / end) and the
 *  packet text body. */

export function CalendarEventView({ resolved }: { resolved: PacketSourceResolved }) {
  const { header, body } = resolved.render;
  return (
    <div className="px-4 sm:px-6 py-4 space-y-4">
      <section className="space-y-1">
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {header.subject || "(no title)"}
        </h1>
        <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5" />
          {header.when ? new Date(header.when).toLocaleString() : "Date unknown"}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 px-3 py-2 text-xs space-y-1">
        {header.organizer && (
          <div className="flex items-center gap-1.5">
            <User className="w-3 h-3 text-slate-400" />
            <span className="text-slate-500 dark:text-slate-400">Organizer:</span>
            <span className="text-slate-800 dark:text-slate-100">{header.organizer}</span>
          </div>
        )}
        {header.location && (
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3 h-3 text-slate-400" />
            <span className="text-slate-800 dark:text-slate-100">{header.location}</span>
          </div>
        )}
        {header.attendees && header.attendees.length > 0 && (
          <div>
            <span className="text-slate-500 dark:text-slate-400 mr-1">Attendees:</span>
            <span className="text-slate-800 dark:text-slate-100">
              {header.attendees.slice(0, 12).join(", ")}
              {header.attendees.length > 12 ? ` +${header.attendees.length - 12} more` : ""}
            </span>
          </div>
        )}
      </section>

      {body.text && (
        <article className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap leading-relaxed">
          {body.text}
        </article>
      )}
    </div>
  );
}
